import React from 'react';
import useAudioDeviceAppState from '../../../../Contexts/AppStateContexts/useAudioDeviceAppState';
import useDefaultDevice from '../../../../Contexts/GlobalContext/Contexts/GlobalStoreContext/Hooks/dualChannelAudio/useDefaultDevice';
import useInputDevices from '../../../../Contexts/GlobalContext/Contexts/GlobalStoreContext/Hooks/dualChannelAudio/useInputDevices';
import useOutputDevices from '../../../../Contexts/GlobalContext/Contexts/GlobalStoreContext/Hooks/dualChannelAudio/useOutputDevices';
import useIsAppEnabled from '../../../ApplicationSyncContext/useIsAppEnabled';
import { LatestDetection } from '../SessionDetectionContextProvider/useSessionDetectionContext';
import { DualChannelDeviceConfig } from '../WebsocketConnectionProviders/WebsocketApiConnectionContext/types';
import { SessionStoreContext } from './useSessionStoreContext';

const DualChannelDevicesContext = React.createContext<DualChannelDeviceConfig | null>(null);

export function useDualChannelDevices() {
  const context = React.useContext(DualChannelDevicesContext);
  return context;
}

function DualChannelDevicesProvider({ children }: { children: React.ReactNode }) {
  const inputDevices = useInputDevices();
  const outputDevices = useOutputDevices();
  const { state } = useAudioDeviceAppState();
  const inputDevice = useDefaultDevice(state?.input, inputDevices.data ?? []);
  const outputDevice = useDefaultDevice(state?.output, outputDevices.data ?? []);

  const value = React.useMemo(() => {
    if (!inputDevices.data || !outputDevices.data) {
      return null;
    }

    return {
      input: {
        options: inputDevices.data,
        selected: inputDevice?.id,
      },
      output: {
        options: outputDevices.data,
        selected: outputDevice?.id,
      },
    };
  }, [inputDevices.data, outputDevices.data, inputDevice, outputDevice]);

  return (
    <DualChannelDevicesContext.Provider value={value}>
      {children}
    </DualChannelDevicesContext.Provider>
  );
}

function DevicesProvider({ children }: { children: React.ReactNode }) {
  const isAppEnabled = useIsAppEnabled();

  if (isAppEnabled) {
    return <DualChannelDevicesProvider>{children}</DualChannelDevicesProvider>;
  }

  return <DualChannelDevicesContext.Provider value={null}>{children}</DualChannelDevicesContext.Provider>;
}

export default function SessionStoreContextProvider({ children }: { children: React.ReactNode }) {
  const topicMentions = React.useState<{ clientMentions: string[], agentMentions: string[] }>({ clientMentions: [], agentMentions: [] });
  const latestDetection = React.useState<LatestDetection | null>(null);
  const detectionHistory = React.useState<(LatestDetection & { cardTitle: string, commandTitle: string })[]>([]);
  const cardPin = React.useState<number | null>(null);
  const context = React.useState<string | null>(null);
  const sessionId = React.useState<string | null>(null);
  const terminating = React.useState<string | null>(null);
  const conversationEngaged = React.useState<boolean>(false);
  const sessionLoading = React.useState<boolean>(false);
  const focusMode = React.useState<boolean>(false);

  const value = React.useMemo(() => ({
    topicMentions,
    latestDetection,
    cardPin,
    context,
    sessionId,
    terminating,
    conversationEngaged,
    sessionLoading,
    detectionHistory,
    focusMode,
  }), [topicMentions, latestDetection, cardPin, context, sessionId, terminating, conversationEngaged, sessionLoading, detectionHistory, focusMode]);

  return (
    <SessionStoreContext.Provider value={value}>
      <DevicesProvider>
        {children}
      </DevicesProvider>
    </SessionStoreContext.Provider>
  );
}
